import { useEffect, useMemo, useRef, useState } from "react";
import {
    ADAPTATION_OPTIONS,
    FIELD_OF,
    filterOptions,
    GENDER_LABELS,
    MONTHS,
} from "./employeeFilters";

/**
 * Панель фильтров таблицы сотрудников: отдел, должность, юрлицо, график, пол (несколько значений),
 * дата выхода с/по, стаж и возраст от/до, месяц рождения, адаптация, учётка ERP.
 * Всё сочетается. Чат 4, правка №15 (MR 4). [Claude] 2026-11-02
 */
const inputCls = "w-full rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-sm outline-none focus:border-amber-400";
const labelCls = "mb-1 block text-xs font-semibold text-slate-500";

function MultiSelect({ label, options, value = [], onChange, testId, render = (v) => v }) {
    const [open, setOpen] = useState(false);
    const [q, setQ] = useState("");
    const ref = useRef(null);
    useEffect(() => {
        if (!open) return undefined;
        const close = (event) => { if (ref.current && !ref.current.contains(event.target)) setOpen(false); };
        document.addEventListener("mousedown", close);
        return () => document.removeEventListener("mousedown", close);
    }, [open]);
    const chosen = value.map(String);
    const shown = options.filter((o) => !q || String(render(o.value)).toLocaleLowerCase("ru").includes(q.toLocaleLowerCase("ru")));
    const toggle = (v) => onChange(chosen.includes(String(v)) ? value.filter((x) => String(x) !== String(v)) : [...value, v]);
    const text = !value.length ? "Все" : value.length === 1 ? render(value[0]) : `${render(value[0])} +${value.length - 1}`;
    return <div className="relative" ref={ref}>
        <span className={labelCls}>{label}</span>
        <button type="button" onClick={() => setOpen((v) => !v)} className={`${inputCls} flex items-center justify-between gap-2 text-left ${value.length ? "border-amber-300 bg-amber-50" : ""}`} data-testid={testId} aria-expanded={open}>
            <span className="truncate">{text}</span><span className="text-slate-400">▾</span>
        </button>
        {open ? <div className="absolute left-0 z-40 mt-1 w-64 rounded-xl border border-slate-200 bg-white p-2 shadow-lg" data-testid={`${testId}-menu`}>
            {options.length > 8 ? <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Найти…" className={`${inputCls} mb-2`} /> : null}
            <div className="max-h-60 overflow-auto">
                {shown.map((o) => <label key={String(o.value)} className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1 text-sm hover:bg-slate-50">
                    <input type="checkbox" checked={chosen.includes(String(o.value))} onChange={() => toggle(o.value)} aria-label={String(render(o.value))} />
                    <span className="flex-1 truncate">{render(o.value)}</span>
                    {o.count != null ? <span className="text-xs text-slate-400">{o.count}</span> : null}
                </label>)}
                {!shown.length ? <p className="px-2 py-1 text-xs text-slate-400">Нет значений</p> : null}
            </div>
            {value.length ? <button type="button" onClick={() => onChange([])} className="mt-1 w-full rounded-lg px-2 py-1 text-left text-xs text-slate-500 hover:bg-slate-50">Снять выбор</button> : null}
        </div> : null}
    </div>;
}

function Range({ label, min, max, onMin, onMax, unit, testId, type = "number" }) {
    return <div>
        <span className={labelCls}>{label}</span>
        <div className="flex items-center gap-1.5">
            <input type={type} min={type === "number" ? 0 : undefined} value={min ?? ""} onChange={(e) => onMin(e.target.value)} placeholder="от" className={inputCls} data-testid={`${testId}-from`} aria-label={`${label} от`} />
            <span className="text-slate-300">—</span>
            <input type={type} min={type === "number" ? 0 : undefined} value={max ?? ""} onChange={(e) => onMax(e.target.value)} placeholder="до" className={inputCls} data-testid={`${testId}-to`} aria-label={`${label} до`} />
            {unit ? <span className="shrink-0 text-xs text-slate-400">{unit}</span> : null}
        </div>
    </div>;
}

export default function EmployeeFiltersPanel({ rows, filters = {}, onChange, onReset }) {
    const set = (key, value) => onChange({ ...filters, [key]: value });
    const opts = useMemo(() => Object.fromEntries(Object.entries(FIELD_OF).map(([key, field]) => [key, filterOptions(rows, field)])), [rows]);
    const empty = (v) => (v === "" ? "Не заполнено" : v);
    return <section className="border-b border-slate-100 bg-slate-50/60 px-4 py-3" data-testid="employee-filters-panel">
        <div className="grid gap-3 grid-cols-[repeat(auto-fill,minmax(200px,1fr))]">
            <MultiSelect label="Отдел" options={opts.departments} value={filters.departments} onChange={(v) => set("departments", v)} testId="filter-departments" render={empty} />
            <MultiSelect label="Должность" options={opts.positions} value={filters.positions} onChange={(v) => set("positions", v)} testId="filter-positions" render={empty} />
            <Range label="Дата выхода" type="date" min={filters.hired_from} max={filters.hired_to} onMin={(v) => set("hired_from", v)} onMax={(v) => set("hired_to", v)} testId="filter-hired" />
            <Range label="Стаж" min={filters.tenure_min} max={filters.tenure_max} onMin={(v) => set("tenure_min", v)} onMax={(v) => set("tenure_max", v)} unit="мес." testId="filter-tenure" />
            <MultiSelect label="Месяц рождения" options={MONTHS.map((_, i) => ({ value: i + 1 }))} value={filters.birth_months} onChange={(v) => set("birth_months", v)} testId="filter-birth-months" render={(m) => MONTHS[Number(m) - 1]} />
            <Range label="Возраст" min={filters.age_min} max={filters.age_max} onMin={(v) => set("age_min", v)} onMax={(v) => set("age_max", v)} unit="лет" testId="filter-age" />
            <MultiSelect label="Адаптация" options={ADAPTATION_OPTIONS.map(([value]) => ({ value }))} value={filters.adaptation} onChange={(v) => set("adaptation", v)} testId="filter-adaptation" render={(v) => Object.fromEntries(ADAPTATION_OPTIONS)[v] || v} />
            <MultiSelect label="Юрлицо" options={opts.legal_entities} value={filters.legal_entities} onChange={(v) => set("legal_entities", v)} testId="filter-legal-entities" render={empty} />
            <MultiSelect label="График" options={opts.schedules} value={filters.schedules} onChange={(v) => set("schedules", v)} testId="filter-schedules" render={empty} />
            <MultiSelect label="Пол" options={opts.genders} value={filters.genders} onChange={(v) => set("genders", v)} testId="filter-genders" render={(v) => (v === "" ? "Не заполнено" : GENDER_LABELS[v] || v)} />
            <div>
                <span className={labelCls}>Учётка ERP</span>
                <select value={filters.erp || ""} onChange={(e) => set("erp", e.target.value)} className={inputCls} data-testid="filter-erp">
                    <option value="">Все</option>
                    <option value="yes">Есть</option>
                    <option value="no">Нет</option>
                </select>
            </div>
            <div className="flex items-end">
                <button type="button" onClick={onReset} className="rounded-lg px-3 py-1.5 text-sm font-semibold text-slate-500 hover:bg-white hover:text-slate-800" data-testid="filters-reset">Сбросить всё</button>
            </div>
        </div>
    </section>;
}
